import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Generate or retrieve a persistent session ID
const getSessionId = () => {
  let sid = sessionStorage.getItem('mando_session_id');
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('mando_session_id', sid);
  }
  return sid;
};

const getDeviceType = () => {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
};

const trackEvent = async (eventData) => {
  try {
    await fetch(`${API_URL}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    });
  } catch {
    // Silently fail - never break the user experience
  }
};

/**
 * useAnalytics - tracks pageview on mount + time_on_page on unmount
 * @param {string} pageTitle - Human readable page title
 */
const useAnalytics = (pageTitle = '') => {
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const sessionId = getSessionId();
    const pageUrl = window.location.pathname;
    const referrer = document.referrer || null;
    const deviceType = getDeviceType();

    // Track pageview
    trackEvent({
      event_type: 'pageview',
      page_url: pageUrl,
      page_title: pageTitle,
      referrer,
      device_type: deviceType,
      session_id: sessionId,
    });

    startTimeRef.current = Date.now();

    // Track time on page on unmount
    return () => {
      const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeOnPage > 3) { // Only track if user spent more than 3 seconds
        trackEvent({
          event_type: 'time_on_page',
          page_url: pageUrl,
          page_title: pageTitle,
          time_on_page: timeOnPage,
          session_id: sessionId,
          device_type: deviceType,
        });
      }
    };
  }, [pageTitle]);

  // Return a function to track CTA clicks manually
  const trackCTA = (ctaName, section = '') => {
    trackEvent({
      event_type: 'cta_click',
      section: ctaName,
      page_url: window.location.pathname,
      session_id: getSessionId(),
      device_type: getDeviceType(),
    });
  };

  const trackSectionView = (sectionName) => {
    trackEvent({
      event_type: 'section_view',
      section: sectionName,
      page_url: window.location.pathname,
      session_id: getSessionId(),
      device_type: getDeviceType(),
    });
  };

  return { trackCTA, trackSectionView };
};

export default useAnalytics;
export { trackEvent, getSessionId };
